/**
 * Billing tab on the project detail page.
 *
 * Two main sections:
 *   1. Bank Guarantees — list with status badges, inline date editing, create
 *      button (Admin), replaces-chain visualization.
 *   2. Part-level billing stages (14-18) — date entry per part, gated on
 *      part being delivered (stage 13 cleared). Stage 16 (Invoice Submitted)
 *      additionally requires an ACCEPTED Bank Guarantee.
 */
import { useEffect, useState } from 'react';
import { Banknote, CreditCard, Pencil, Plus } from 'lucide-react';
import { formatCurrency, formatDate } from '@procurement/shared';
import { Alert } from '@/components/ui/alert';
import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { DmyDateInput } from '@/components/forms/dmy-date-input';
import { Card } from '@/components/ui/card';
import { EmptyState } from '@/components/ui/empty-state';
import { ExportCsvButton } from '@/components/ui/export-csv-button';
import { FormField, Input } from '@/components/ui/form-field';
import { Modal } from '@/components/ui/modal';
import { Spinner } from '@/components/ui/spinner';
import { StatusBadge } from '@/components/ui/status-badge';
import { AttachmentBadge } from '@/components/attachments/attachment-badge';
import { useCurrentUser } from '@/lib/auth';
import { useAttachmentCounts } from '@/lib/attachments-queries';
import { isLockedForUser } from '@/lib/closure-lock';
import {
  useBankGuarantees,
  useCreateBg,
  useCreatePaymentEvent,
  usePartStageDates,
  usePaymentEvents,
  useProjectItems,
  useUpdateBg,
  useUpsertPartStageDate,
  type BankGuarantee,
  type PartStageNumber,
  type PaymentEvent,
  type PaymentEventType,
  type ProjectItem,
} from '@/lib/pipeline-queries';
import type { Project } from '@/lib/project-queries';

interface Props {
  project: Project;
}

export function ProjectBillingTab({ project }: Props) {
  const me = useCurrentUser();
  const isAdmin = me.data?.user.role === 'ADMIN';
  // Spec §9 / §13 — Admin retains edit access on closed projects.
  const locked = isLockedForUser(project, isAdmin);
  const bgs = useBankGuarantees(project.id);
  const items = useProjectItems(project.id);
  // E26 — per-entity attachment counts for the 📎 N badges
  const counts = useAttachmentCounts(project.id);
  const [showCreateBg, setShowCreateBg] = useState(false);

  if (bgs.isLoading || items.isLoading) {
    return (
      <div className="flex justify-center py-12">
        <Spinner className="h-6 w-6 text-secondary" />
      </div>
    );
  }

  const bgList = bgs.data ?? [];
  // Billing stages are tracked at the part level (variant B + variant A).
  // Sub-parts roll up; they don't carry billing dates themselves.
  const billableParts = (items.data ?? []).filter((i) => i.itemType === 'PART');
  const hasAcceptedBg = bgList.some((bg) => bg.status === 'ACCEPTED');

  return (
    <div className="space-y-6">
      {/* ----- Bank Guarantees ----- */}
      <Card
        title="Bank Guarantees"
        actions={
          isAdmin && !locked ? (
            <Button
              variant="secondary"
              size="compact"
              leftIcon={<Plus className="h-3.5 w-3.5" />}
              onClick={() => setShowCreateBg(true)}
            >
              New BG
            </Button>
          ) : undefined
        }
        bodyClassName="p-0"
      >
        {bgList.length === 0 ? (
          <EmptyState
            icon={<Banknote className="h-6 w-6" />}
            title="No bank guarantees yet"
            body="Stage 16 (Invoice Submitted) requires an ACCEPTED Bank Guarantee. Create the first BG when the supplier provides it."
          />
        ) : (
          <div className="divide-y divide-border-default">
            {bgList.map((bg) => (
              <BgRow
                key={bg.id}
                project={project}
                bg={bg}
                replacesChain={bgList}
                locked={locked}
                attachmentCount={counts.data?.byBg[bg.id] ?? 0}
              />
            ))}
          </div>
        )}
      </Card>

      {/* ----- Part-level billing stages ----- */}
      <Card
        title="Part billing stages (14-18)"
        actions={
          billableParts.length > 0 ? (
            <ExportCsvButton
              href={`/api/exports/projects/${project.id}/billing`}
              fallbackFilename="billing.csv"
            />
          ) : undefined
        }
        bodyClassName="p-0"
      >
        {billableParts.length === 0 ? (
          <EmptyState
            title="No parts on this project yet"
            body="Once the parts list is finalised and deliveries complete, billing stages can be recorded here."
          />
        ) : (
          <div className="divide-y divide-border-default">
            {billableParts.map((p) => (
              <PartBillingRow key={p.id} project={project} part={p} hasAcceptedBg={hasAcceptedBg} />
            ))}
          </div>
        )}
      </Card>

      {showCreateBg ? (
        <CreateBgModal project={project} existing={bgList} onClose={() => setShowCreateBg(false)} />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Bank Guarantee row
// ---------------------------------------------------------------------------

function BgRow({
  project,
  bg,
  replacesChain,
  locked,
  attachmentCount,
}: {
  project: Project;
  bg: BankGuarantee;
  replacesChain: BankGuarantee[];
  locked: boolean;
  attachmentCount: number;
}) {
  const update = useUpdateBg(project.id);
  const replacedBy = replacesChain.find((b) => b.replacesBgId === bg.id);
  const replaces = bg.replacesBgId ? replacesChain.find((b) => b.id === bg.replacesBgId) : null;

  const onSet = (field: keyof BankGuarantee, value: string): void => {
    update.mutate({
      bgId: bg.id,
      rowVersion: bg.rowVersion,
      [field]: value || null,
    });
  };

  // Spec §13 — "Set BG dates on existing instrument | ✓ | ✓": both roles
  // can edit BG dates on an open project (and Admin keeps access on closed).
  // Replaced BGs are historical and not editable by anyone.
  const canEdit = !locked && bg.status !== 'REPLACED';

  return (
    <div className="p-4">
      <div className="mb-3 flex items-center justify-between gap-3">
        <div className="flex items-center gap-2">
          <StatusBadge kind="bg" value={bg.status} />
          <span className="text-sm font-medium tabular-nums">{formatCurrency(bg.amountPaisa)}</span>
          {replaces ? (
            <Badge tone="neutral">Replaces BG {formatCurrency(replaces.amountPaisa)}</Badge>
          ) : null}
          {replacedBy ? <Badge tone="neutral">Replaced by newer BG</Badge> : null}
        </div>
        {/* E26 — inline attachment count. Click is wired in Patch 2 once
            the popover view exists; for now it's an at-a-glance signal. */}
        <AttachmentBadge count={attachmentCount} />
      </div>

      <div className="grid grid-cols-2 gap-2 lg:grid-cols-4">
        <DateCell
          label="Submitted"
          value={bg.submittedDate}
          disabled={!canEdit}
          onChange={(v) => onSet('submittedDate', v)}
        />
        <DateCell
          label="Accepted"
          value={bg.acceptedDate}
          disabled={!canEdit || !bg.submittedDate}
          onChange={(v) => onSet('acceptedDate', v)}
        />
        <DateCell
          label="Applied (encashed)"
          value={bg.appliedDate}
          disabled={!canEdit || !bg.acceptedDate}
          onChange={(v) => onSet('appliedDate', v)}
        />
        <DateCell
          label="Released"
          value={bg.releasedDate}
          disabled={!canEdit || !bg.acceptedDate}
          onChange={(v) => onSet('releasedDate', v)}
        />
      </div>

      {update.error ? (
        <Alert tone="danger" className="mt-3">
          {update.error.message}
        </Alert>
      ) : null}
    </div>
  );
}

// Patch P5 — once a date is recorded, the cell renders as static text with a
// pencil button. Clicking the pencil enters edit mode for that one field;
// the user can then change the date and onBlur saves and exits. This makes
// recorded dates feel like "facts" rather than open inputs that fat-fingers
// can drift, while leaving unrecorded dates immediately editable.
function DateCell({
  label,
  value,
  disabled,
  onChange,
}: {
  label: string;
  value: string | null;
  disabled: boolean;
  onChange: (v: string) => void;
}) {
  const [editing, setEditing] = useState(false);
  // When the persisted value flips back to null (e.g. an admin cleared it
  // upstream) cancel any in-flight edit so we don't show a stale input.
  useEffect(() => {
    if (value === null) setEditing(false);
  }, [value]);

  const hasValue = value !== null && value !== '';
  // Show the input directly when: no value yet (nothing to protect), OR
  // editing toggled on, OR the cell is disabled (we still show the disabled
  // input so the user can see why it's disabled).
  const showInput = !hasValue || editing || disabled;

  return (
    <div>
      <label className="block text-[11px] font-semibold uppercase tracking-wide text-tertiary">
        {label}
      </label>
      {showInput ? (
        <DmyDateInput
          value={value ?? ''}
          disabled={disabled}
          autoFocus={editing}
          onChange={onChange}
          onBlur={() => setEditing(false)}
          showValidationMessage={false}
          className="mt-1 h-8"
        />
      ) : (
        <div className="mt-1 flex h-8 items-center justify-between gap-1 rounded-sm border border-border-subtle bg-subtle/40 px-2 text-xs tabular-nums text-primary">
          <span>{formatDate(value)}</span>
          <button
            type="button"
            onClick={() => setEditing(true)}
            aria-label={`Edit ${label}`}
            className="rounded-sm p-0.5 text-tertiary hover:bg-surface hover:text-accent-strong"
          >
            <Pencil className="h-3 w-3" strokeWidth={1.5} />
          </button>
        </div>
      )}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Part billing stages row — stages 14, 15, 16, 17, 18
// ---------------------------------------------------------------------------

const BILLING_STAGES: { num: PartStageNumber; label: string }[] = [
  { num: 14, label: '14. I-Note received' },
  { num: 15, label: '15. CRV issued' },
  { num: 16, label: '16. Invoice submitted' },
  { num: 17, label: '17. Cheque released' },
  { num: 18, label: '18. Cheque cleared' },
];

function PartBillingRow({
  project,
  part,
  hasAcceptedBg,
}: {
  project: Project;
  part: ProjectItem;
  hasAcceptedBg: boolean;
}) {
  const dates = usePartStageDates(project.id, part.id);
  const upsert = useUpsertPartStageDate(project.id, part.id);
  const [showPayment, setShowPayment] = useState(false);
  const me = useCurrentUser();
  const isAdmin = me.data?.user.role === 'ADMIN';

  if (dates.isLoading) {
    return (
      <div className="flex justify-center px-4 py-6">
        <Spinner className="h-4 w-4 text-secondary" />
      </div>
    );
  }

  const byStage = new Map<number, string>();
  for (const d of dates.data ?? []) byStage.set(d.stageNumber, d.completedDate);

  // Gate stage 14: part must have cleared stage 13 (functional check).
  // currentStageNumber reflects the highest stage with a CLOSED fact, so
  // stage 13 is cleared iff currentStageNumber >= 13.
  const stage13Cleared = part.currentStageNumber >= 13;
  // Spec §9 — closed projects lock edits against Assistants only.
  const locked = isLockedForUser(project, isAdmin);
  const canRecordPayment = !locked && stage13Cleared && hasAcceptedBg;

  const enabledFor = (num: PartStageNumber, idx: number): boolean => {
    if (locked) return false;
    if (num === 14) return stage13Cleared;
    if (num === 16 && !hasAcceptedBg) return false; // invoice gate
    const prev = BILLING_STAGES[idx - 1];
    if (!prev) return true;
    return byStage.has(prev.num);
  };

  return (
    <div className="p-4">
      <div className="mb-3 flex flex-col gap-3 lg:flex-row lg:items-start lg:justify-between">
        <div className="min-w-0">
          <div className="flex flex-wrap items-center gap-2">
            <span className="font-mono text-xs text-secondary tabular-nums">Sr {part.srNumber}</span>
            <span className="text-sm font-medium text-primary">{part.name}</span>
            {!stage13Cleared ? (
              <Badge tone="neutral">Awaiting delivery</Badge>
            ) : !hasAcceptedBg ? (
              <Badge tone="warning">Invoice locked — no accepted BG</Badge>
            ) : null}
          </div>
          <div className="mt-1 flex flex-wrap gap-x-4 gap-y-1 text-xs text-secondary">
            <span>Invoiced: {formatCurrency(part.invoicedAmountPaisa ?? 0)}</span>
            <span>Remaining invoice: {formatCurrency(part.remainingInvoiceAmountPaisa ?? 0)}</span>
            <span>Released: {formatCurrency(part.chequeReleasedAmountPaisa ?? 0)}</span>
            <span>Cleared: {formatCurrency(part.chequeClearedAmountPaisa ?? 0)}</span>
            <span>Remaining payment: {formatCurrency(part.remainingPaymentAmountPaisa ?? 0)}</span>
          </div>
        </div>
        {canRecordPayment ? (
          <Button
            variant="secondary"
            size="compact"
            leftIcon={<CreditCard className="h-3.5 w-3.5" />}
            onClick={() => setShowPayment(true)}
          >
            Record billing event
          </Button>
        ) : null}
      </div>

      <div className="grid grid-cols-2 gap-2 lg:grid-cols-5">
        {BILLING_STAGES.map((s, idx) => {
          const enabled = enabledFor(s.num, idx);
          const existing = byStage.get(s.num) ?? null;
          return (
            <DateCell
              key={s.num}
              label={s.label}
              value={existing}
              disabled={!enabled}
              onChange={(v) => {
                if (!v) return;
                upsert.mutate({ stageNumber: s.num, completedDate: v });
              }}
            />
          );
        })}
      </div>

      {upsert.error ? (
        <Alert tone="danger" className="mt-3">
          {upsert.error.message}
        </Alert>
      ) : null}

      {showPayment ? (
        <PaymentEventModal project={project} part={part} onClose={() => setShowPayment(false)} />
      ) : null}
    </div>
  );
}

// ---------------------------------------------------------------------------
// Payment event modal — partial invoices, releases, and clearances
// ---------------------------------------------------------------------------

const PAYMENT_EVENT_OPTIONS: { value: PaymentEventType; label: string }[] = [
  { value: 'INVOICE_SUBMITTED', label: 'Invoice submitted' },
  { value: 'CHEQUE_RELEASED', label: 'Cheque released' },
  { value: 'CHEQUE_CLEARED', label: 'Cheque cleared' },
];

function paymentEventLabel(type: PaymentEventType): string {
  return PAYMENT_EVENT_OPTIONS.find((option) => option.value === type)?.label ?? type;
}

function paisaToAmountInput(value: number): string {
  return value > 0 ? String(value / 100) : '';
}

function remainingForPaymentEvent(part: ProjectItem, eventType: PaymentEventType): number {
  if (eventType === 'INVOICE_SUBMITTED') return Math.max(0, part.remainingInvoiceAmountPaisa ?? 0);
  if (eventType === 'CHEQUE_RELEASED') {
    return Math.max(0, (part.invoicedAmountPaisa ?? 0) - (part.chequeReleasedAmountPaisa ?? 0));
  }
  return Math.max(0, (part.chequeReleasedAmountPaisa ?? 0) - (part.chequeClearedAmountPaisa ?? 0));
}

function PaymentEventModal({
  project,
  part,
  onClose,
}: {
  project: Project;
  part: ProjectItem;
  onClose: () => void;
}) {
  const events = usePaymentEvents(project.id, part.id);
  const create = useCreatePaymentEvent(project.id, part.id);
  const [eventType, setEventType] = useState<PaymentEventType>('INVOICE_SUBMITTED');
  const [amount, setAmount] = useState(() => paisaToAmountInput(remainingForPaymentEvent(part, 'INVOICE_SUBMITTED')));
  const [eventDate, setEventDate] = useState('');
  const [referenceNumber, setReferenceNumber] = useState('');
  const [remarks, setRemarks] = useState('');

  const remainingPaisa = remainingForPaymentEvent(part, eventType);
  const amountNumber = Number(amount);
  const amountPaisa = Number.isFinite(amountNumber) ? Math.round(amountNumber * 100) : 0;
  const canSubmit = amountPaisa > 0 && amountPaisa <= remainingPaisa && eventDate.trim().length > 0;

  const onEventTypeChange = (next: PaymentEventType): void => {
    setEventType(next);
    setAmount(paisaToAmountInput(remainingForPaymentEvent(part, next)));
  };

  const submit = (): void => {
    if (!canSubmit) return;
    create.mutate(
      {
        eventType,
        amountPaisa,
        eventDate,
        referenceNumber: referenceNumber.trim() ? referenceNumber.trim().toLocaleUpperCase('en-US') : null,
        remarks: remarks.trim() ? remarks.trim().toLocaleUpperCase('en-US') : null,
      },
      { onSuccess: onClose },
    );
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="Record billing event"
      description={`${part.name} · ${paymentEventLabel(eventType).toLowerCase()}`}
      footer={
        <>
          <Button variant="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button variant="primary" onClick={submit} disabled={!canSubmit} loading={create.isPending}>
            Save billing event
          </Button>
        </>
      }
    >
      {create.error ? (
        <Alert tone="danger" className="mb-3">
          {create.error.message}
        </Alert>
      ) : null}
      {events.error ? (
        <Alert tone="danger" className="mb-3">
          {events.error.message}
        </Alert>
      ) : null}
      {!project.partialPaymentAllowed ? (
        <Alert tone="warning" className="mb-3">
          Partial payment is not allowed for this project. The backend will accept only the full remaining amount for each billing step.
        </Alert>
      ) : null}

      <div className="mb-3 grid gap-2 rounded-sm border border-border-default bg-subtle/40 px-3 py-2 text-xs text-secondary sm:grid-cols-3">
        <span>Invoiced: {formatCurrency(part.invoicedAmountPaisa ?? 0)}</span>
        <span>Released: {formatCurrency(part.chequeReleasedAmountPaisa ?? 0)}</span>
        <span>Cleared: {formatCurrency(part.chequeClearedAmountPaisa ?? 0)}</span>
      </div>

      <FormField label="Billing event" required>
        <select
          value={eventType}
          onChange={(event) => onEventTypeChange(event.target.value as PaymentEventType)}
          className="block h-9 w-full appearance-none rounded-md border border-border-default bg-surface px-3 pr-8 text-sm text-primary"
        >
          {PAYMENT_EVENT_OPTIONS.map((option) => (
            <option key={option.value} value={option.value}>
              {option.label}
            </option>
          ))}
        </select>
      </FormField>
      <div className="grid gap-3 sm:grid-cols-2">
        <FormField
          label="Amount (Rs.)"
          required
          helpText={`Remaining for this event: ${formatCurrency(remainingPaisa)}`}
        >
          <Input
            type="number"
            min={0}
            step="0.01"
            value={amount}
            onChange={(event) => setAmount(event.target.value)}
            className="font-mono tabular-nums"
          />
        </FormField>
        <FormField label="Event date" required>
          <DmyDateInput value={eventDate} onChange={setEventDate} />
        </FormField>
      </div>
      <FormField label="Reference number">
        <Input
          value={referenceNumber}
          onChange={(event) => setReferenceNumber(event.target.value.toLocaleUpperCase('en-US'))}
          placeholder={eventType === 'INVOICE_SUBMITTED' ? 'e.g. INV-001' : 'e.g. CHQ-001'}
        />
      </FormField>
      <FormField label="Remarks">
        <textarea
          value={remarks}
          onChange={(event) => setRemarks(event.target.value.toLocaleUpperCase('en-US'))}
          rows={2}
          className="w-full rounded-md border border-border-default bg-base px-3 py-2 text-sm text-primary outline-none transition focus:border-accent focus:ring-2 focus:ring-accent/20"
          placeholder="Optional billing remarks"
        />
      </FormField>

      <PaymentHistory events={events.data ?? []} loading={events.isLoading} />
    </Modal>
  );
}

function PaymentHistory({ events, loading }: { events: PaymentEvent[]; loading: boolean }) {
  if (loading) {
    return <div className="mt-3 text-xs text-secondary">Loading billing history…</div>;
  }
  if (events.length === 0) {
    return <div className="mt-3 text-xs text-secondary">No billing event records yet.</div>;
  }
  return (
    <div className="mt-4 overflow-hidden rounded-md border border-border-default">
      <table className="min-w-full divide-y divide-border-default text-xs">
        <thead className="bg-subtle text-left text-tertiary">
          <tr>
            <th className="px-2 py-1">Date</th>
            <th className="px-2 py-1">Event</th>
            <th className="px-2 py-1 text-right">Amount</th>
            <th className="px-2 py-1">Reference</th>
            <th className="px-2 py-1">Remarks</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-border-default">
          {events.map((event) => (
            <tr key={event.id}>
              <td className="px-2 py-1 tabular-nums">{formatDate(event.eventDate)}</td>
              <td className="px-2 py-1">{paymentEventLabel(event.eventType)}</td>
              <td className="px-2 py-1 text-right tabular-nums">{formatCurrency(event.amountPaisa)}</td>
              <td className="px-2 py-1">{event.referenceNumber ?? '—'}</td>
              <td className="px-2 py-1">{event.remarks ?? '—'}</td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
}

// ---------------------------------------------------------------------------
// Create BG modal
// ---------------------------------------------------------------------------

function CreateBgModal({
  project,
  existing,
  onClose,
}: {
  project: Project;
  existing: BankGuarantee[];
  onClose: () => void;
}) {
  const create = useCreateBg(project.id);
  const [amount, setAmount] = useState('');
  const [submitted, setSubmitted] = useState('');
  const [replacesBgId, setReplacesBgId] = useState('');

  const replaceable = existing.filter((b) => b.status === 'ACTIVE' || b.status === 'ACCEPTED');

  const required = Number(amount) > 0;

  const submit = (): void => {
    if (!required) return;
    create.mutate(
      {
        amountPaisa: Math.round(Number(amount) * 100),
        ...(submitted ? { submittedDate: submitted } : {}),
        ...(replacesBgId ? { replacesBgId } : {}),
      },
      { onSuccess: onClose },
    );
  };

  return (
    <Modal
      open
      onClose={onClose}
      title="New Bank Guarantee"
      description={`Project ${project.projectNumber}`}
      footer={
        <>
          <Button variant="tertiary" onClick={onClose}>
            Cancel
          </Button>
          <Button
            variant="primary"
            onClick={submit}
            disabled={!required}
            loading={create.isPending}
          >
            Create BG
          </Button>
        </>
      }
    >
      {create.error ? (
        <Alert tone="danger" className="mb-3">
          {create.error.message}
        </Alert>
      ) : null}
      <FormField label="Amount (Rs.)" required>
        <Input
          autoFocus
          type="number"
          min={0}
          step="0.01"
          value={amount}
          onChange={(e) => setAmount(e.target.value)}
        />
      </FormField>
      <FormField label="Submitted date">
        <DmyDateInput value={submitted} onChange={setSubmitted} />
      </FormField>
      {replaceable.length > 0 ? (
        <FormField
          label="Replaces existing BG (optional)"
          helpText={`Marks the prior BG as REPLACED. Available: ${replaceable
            .map((b) => formatDate(b.submittedDate))
            .join(', ')}`}
        >
          <select
            className="block h-9 w-full appearance-none rounded-md border border-border-default bg-surface px-3 pr-8 text-sm text-primary"
            value={replacesBgId}
            onChange={(e) => setReplacesBgId(e.target.value)}
          >
            <option value="">— none —</option>
            {replaceable.map((b) => (
              <option key={b.id} value={b.id}>
                {formatCurrency(b.amountPaisa)} · submitted {formatDate(b.submittedDate)}
              </option>
            ))}
          </select>
        </FormField>
      ) : null}
    </Modal>
  );
}
